// src/components/AdminPanel.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Save, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { generateFingerprint } from '../utils/fingerprint';
import { getAllItems, updateList, uploadImageWithRetry } from '../utils/api'; // updated import
import LoadingSpinner from '../utils/loadingSpinner'
import Footer from "./Footer"

const { v4: uuidv4 } = require("uuid");

const itemId = uuidv4();

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  if (match) return decodeURIComponent(match[2]);
  return null;
}

const AdminPanel = ({ creatorId }) => {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const bottomRef = useRef(null);
  const fileInputRefs = useRef([]);

  // Calculate duplicates
  const duplicates = useMemo(() => {
    const nameCount = {};
    const imageUrlCount = {};
    const result = { names: new Set(), imageUrls: new Set() };

    items.forEach((item, index) => {
      // Check for duplicate names (case-insensitive, ignore empty)
      if (item.name.trim()) {
        const normalizedName = item.name.trim().toLowerCase();
        if (!nameCount[normalizedName]) {
          nameCount[normalizedName] = [];
        }
        nameCount[normalizedName].push(index);
      }

      // Check for duplicate image URLs (ignore empty)
      if (item.imageUrl.trim()) {
        const normalizedUrl = item.imageUrl.trim();
        if (!imageUrlCount[normalizedUrl]) {
          imageUrlCount[normalizedUrl] = [];
        }
        imageUrlCount[normalizedUrl].push(index);
      }
    });

    // Mark indices with duplicates
    Object.values(nameCount).forEach(indices => {
      if (indices.length > 1) {
        indices.forEach(index => result.names.add(index));
      }
    });

    Object.values(imageUrlCount).forEach(indices => {
      if (indices.length > 1) {
        indices.forEach(index => result.imageUrls.add(index));
      }
    });

    return result;
  }, [items]);

  useEffect(() => {    
    const loadList = async () => {
      try {
        setLoading(true);
        
        // Check if demo
        if (listId === 'DEMO123') {
          const demoList = {
            id: 'DEMO123',
            title: 'Favorite Programming Languages',
            description: 'Vote for your favorite programming language',
            items: [
              { id: '1', name: 'JavaScript', elo: 1250, wins: 3, losses: 1, imageUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRfQ4MH3vLA6l72ULn3Up_6undPBcXoERMFcA&s', uploading: false },
              { id: '2', name: 'TypeScript', elo: 1220, wins: 2, losses: 1, imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f5/Typescript.svg/1200px-Typescript.svg.png', uploading: false },
              { id: '3', name: 'Python', elo: 1200, wins: 2, losses: 2, imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c3/Python-logo-notext.svg/1200px-Python-logo-notext.svg.png', uploading: false },
              { id: '4', name: 'React', elo: 1180, wins: 1, losses: 3, imageUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQcR5U16C8yXgBpl7-Bc7Itjx3_LRl425zINA&s', uploading: false }
            ]
          };
          setList(demoList);
          setItems([...demoList.items]);
          setIsDemo(true);
          setLoading(false);
          return;
        }

        // Load from API
        const fingerprint = await generateFingerprint();
        const result = await getAllItems(listId, fingerprint);
        
        // Convert items object to array
        const itemsArray = Object.entries(result.items).map(([id, data]) => ({
          id,
          ...data,
          uploading: false,
          imageFile: null
        }));
        
        setList({
          id: listId,
          publicId: listId,
          title: result.meta.name || 'Untitled List',
          items: itemsArray,
          totalComparisons: result.meta.total_comparisons || 0,
          adminCode: result.permissions?.Password || null
        });
        setItems([...itemsArray]);
      } catch (err) {
        console.error('Error loading list:', err);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadList();
  }, [listId, navigate]);

  const addItem = () => {
    if (items.length >= 500) {
      alert('You cannot add more than 500 items.');
      return;
    }

    setItems([...items, { id: uuidv4(), name: '', imageUrl: '', rating: 1200, wins: 0, losses: 0, uploading: false, imageFile: null }]);
    
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
      // Remove the corresponding file input ref
      fileInputRefs.current = fileInputRefs.current.filter((_, i) => i !== index);
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  // Image upload functionality
  const handleImageUpload = async (index, file) => {
    if (!file) return;

    try {
      // Update item to show uploading state
      updateItem(index, 'uploading', true);
      
      console.log('Starting upload for file:', file.name, 'Size:', file.size, 'Type:', file.type);
      
      // Use the retry version of uploadImage with progress tracking
      const result = await uploadImageWithRetry(file, 3, (progress) => {
        console.log(`Upload progress: ${Math.round(progress)}%`);
      });
      
      console.log("Upload result:", result);
      
      if (result.success && result.imageUrl) {
        // Update the item with the uploaded image URL
        updateItem(index, 'imageUrl', result.imageUrl);
        updateItem(index, 'imageFile', file);
        console.log('Upload successful, URL:', result.imageUrl);
      } else {
        throw new Error('Upload failed - no image URL returned');
      }
      
    } catch (error) {
      console.error('Error uploading image:', error);
      
      // Provide user-friendly error messages
      let userMessage = 'Failed to upload image. ';
      
      if (error.message.includes('too large')) {
        userMessage += 'The file is too large. Please choose an image smaller than 5MB.';
      } else if (error.message.includes('Only image files')) {
        userMessage += 'Please select an image file (JPG, PNG, GIF, etc.).';
      } else if (error.message.includes('Network error') || error.message.includes('timed out')) {
        userMessage += 'Network error. Please check your connection and try again.';
      } else if (error.message.includes('incomplete') || error.message.includes('corrupted')) {
        userMessage += 'The upload was incomplete. Please try again.';
      } else {
        userMessage += 'Please try again in a moment.';
      }
      
      alert(userMessage);
      
      // Clear the file input on error
      if (fileInputRefs.current[index]) {
        fileInputRefs.current[index].value = '';
      }
      
    } finally {
      // Remove uploading state
      updateItem(index, 'uploading', false);
    }
  };

  const handleFileSelect = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageUpload(index, file);
    }
  };

  const removeImage = (index) => {
    updateItem(index, 'imageUrl', '');
    updateItem(index, 'imageFile', null);
    // Reset the file input
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].value = '';
    }
  };

  const handleSave = async () => {
    const validItems = items.filter(item => item.name.trim());
    if (validItems.length < 2) {
      alert('Please have at least 2 items in your list.');
      return;
    }

    // Check if any images are still uploading
    const uploadingItems = items.filter(item => item.uploading);
    if (uploadingItems.length > 0) {
      alert('Please wait for all images to finish uploading before saving changes.');
      return;
    }

    // Check for duplicates and ask for confirmation
    if (duplicates.names.size > 0 || duplicates.imageUrls.size > 0) {
      const duplicateTypes = [];
      if (duplicates.names.size > 0) duplicateTypes.push('item names');
      if (duplicates.imageUrls.size > 0) duplicateTypes.push('image URLs');
      
      const confirmed = window.confirm(
        `Warning: You have duplicate ${duplicateTypes.join(' and ')} in your list. Are you sure you want to save the changes?`
      );
      if (!confirmed) {
        return;
      }
    }

    if (isDemo) {
      alert('This is a demo - changes cannot be saved');
      return;
    }

    setSaving(true);
    try {
      const storedCodeFromCookie = getCookie(`${listId}`);
      
      // Use the new updateList API function
      const result = await updateList(listId, {
        items: validItems,
        _adminCode: storedCodeFromCookie
      });
      
      alert('List updated successfully!');
      navigate(`/list/${listId}`);
    } catch (error) {
      console.error('Error updating list:', error);
      alert('Failed to update list. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />
  if (!list) return <div className="container"><div className="error">List not found</div></div>;

  return (
    <div className="container">
      <button onClick={() => navigate(`/list/${listId}`)} className="back-btn">
        <ArrowLeft size={20} />
        Back to List
      </button>

      <button onClick={() => navigate('/imageRules')} className="create-btn">
        Rules
      </button>

      <div className="card">
        <h1 className="title">Admin Panel</h1>
        <h2>{list.title}</h2>
        {isDemo && <p className="demo-notice">Demo Mode - Changes won't be saved</p>}

        <div className="form-group">
          <label>Manage Items</label>
          {items.map((item, index) => (
            <div key={item.id || index} className="item-group item-group-container">
              <div className="inputs-and-image-container">
                {/* Left side: Input fields */}
                <div className="inputs-container">
                  {/* Name Input */}
                  <div className="name-input-wrapper">
                    <input
                      type="text"
                      placeholder="Item name"
                      value={item.name}
                      onChange={(e) => updateItem(index, 'name', e.target.value)}
                      className={`input ${duplicates.names.has(index) ? 'input-error' : ''}`}
                      required
                    />
                    {duplicates.names.has(index) && (
                      <div className="duplicate-warning">
                        <AlertTriangle size={16} />
                        Warning: Duplicate name
                      </div>
                    )}
                  </div>

                  {/* URL Input */}
                  <div className="url-input-wrapper">
                    <input
                      type="url"
                      placeholder="Or enter image URL"
                      value={item.imageUrl}
                      onChange={(e) => updateItem(index, 'imageUrl', e.target.value)}
                      className={`input ${duplicates.imageUrls.has(index) ? 'input-error' : ''}`}
                    />
                    {duplicates.imageUrls.has(index) && (
                      <div className="duplicate-warning">
                        <AlertTriangle size={16} />
                        Warning: Duplicate image URL
                      </div>
                    )}
                    <div className="url-input-help-text">
                      Upload an image or paste a URL here
                    </div>
                  </div>
                </div>

                {/* Right side: Image upload/preview */}
                <div className="image-container">
                  <div className="image-upload-area">
                    {item.uploading ? (
                      <div className="upload-spinner-container">
                        <div className="upload-spinner" />
                        <span className="upload-spinner-text">Uploading...</span>
                      </div>
                    ) : item.imageUrl ? (
                      <div className="image-preview-container">
                        <img 
                          src={item.imageUrl} 
                          alt={item.name || 'Item image'}
                          className="image-preview"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="image-error-fallback">
                          Failed to load image
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="image-remove-button"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="image-upload-placeholder" onClick={() => fileInputRefs.current[index]?.click()}>
                        <ImageIcon size={32} color="#a0aec0" className="image-upload-icon" />
                        <div className="image-upload-text">Click to upload</div>
                      </div>
                    )}
                    
                    <input
                      ref={el => fileInputRefs.current[index] = el}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileSelect(index, e)}
                      className="hidden-file-input"
                    />
                  </div>
                </div>

                {/* Remove button */}
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="btn btn-danger-small"
                    style={{ alignSelf: 'flex-start' }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
          
          <button type="button" onClick={addItem} className="btn btn-outline">
            <Plus size={16} />
            Add Item
          </button>
          <div ref={bottomRef} />
        </div>

        <button
          onClick={handleSave}
          disabled={saving || isDemo}
          className="btn btn-primary"
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <Footer />
    </div>
  );
};

export default AdminPanel;